import React from "react";
import { connect } from "react-redux";
import styled from "styled-components";
import { i18n, navigate, notification } from "../../services";
import { AppState } from "../../store";
import {
    Notification,
    Select,
    Switch,
    ButtonActionGroup,
    Button,
    BTN_SMALL,
    TYPE_SECONDARY,
    TYPE_SUCCESS,
} from "../../lib";
import { ComparePage } from "../../pages/CreateMergePage";

const Container = styled.div`
    display: flex;
    flex-direction: column;
    flex-wrap: nowrap;
    align-items: center;
    user-select: none;
`;

const Heading = styled.h2`
    font-size: 30px;
    margin: 20px auto 20px auto;
`;

const Description = styled.div`
    font-size: 16px;
`;

const NoChanges = styled.div`
    width: 50%;
    margin-top: 40px;
    text-align: center;
    font-size: 18px;
    padding: 15px 15px;
    border: 1px solid #ebcccc;
    color: #a94442;
    background-color: #f2dede;
`;

const AlreadyUpToDate = styled.div`
    margin: 10px 0 0 0;
    height: 60px;
    line-height: 50px;
    padding: 5px;
    text-align: center;
    color: #000;
    background-color: #bcdff1;
`;

const Fields = styled.div`
    position: relative;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    width: 100%;
    justify-content: space-between;
`;
const FieldContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 49%;
`;

const FieldTitle = styled.div`
    font-size: 18px;
    font-family: urdu;
    font-weight: 500;
`;

const SwitchContainer = styled.div`
    cursor: pointer;
    position: absolute;
    right: -80px;
    bottom: 0px;
`;

const mapState = (state: AppState) => ({ user: state.user });
const mapDispatch = {};

type StateProps = ReturnType<typeof mapState>;
type DispatchProps = typeof mapDispatch;
type OwnProps = {
    page_contents_id: number;
    onCompare: (a: number, b: number) => void;
    onCompareClear: () => void;
    onConfirm: (src_page_id: number, src: number, dst: number) => void;
    pages: ComparePage[];
};

type Props = StateProps & DispatchProps & OwnProps;
type State = {
    srcAll: boolean;
    src: number;
    dst: number;
};

class Header extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = {
            srcAll: false,
            src: props.page_contents_id || -1,
            dst: -1,
        };
    }

    onChangeSrc = (_name: string, value: string) => {
        notification.clear();

        this.setState({ src: parseInt(value), dst: -1 });
        this.props.onCompareClear();
    };

    onChangeDst = (_name: string, value: string) => {
        notification.clear();

        this.setState({ dst: parseInt(value) }, () => {
            this.props.onCompare(this.state.src, this.state.dst);
        });
    };

    src() {
        const { srcAll } = this.state;
        const { user, pages } = this.props;
        const values = [-1];
        const labels = [i18n.get("form.selectEmptyLabel")];

        let status;
        for (const p of pages) {
            // @ts-ignore
            if (srcAll || p.user_id === user.get("id")) {
                values.push(p.page_contents_id);

                status = i18n.get(`userDocForm.status${p.user_status}`);
                labels.push(`${status} / ${p.user_name} / ${p.page_name}`);
            }
        }

        return (
            <FieldContainer>
                <FieldTitle>{i18n.get("merge.src")}</FieldTitle>
                <Select
                    key="src-page"
                    id="src_page"
                    name="src_page"
                    values={values.map((v) => v.toString())}
                    labels={labels}
                    fullWidth={true}
                    value={this.state.src.toString()}
                    onChange={this.onChangeSrc}
                />
            </FieldContainer>
        );
    }

    dst() {
        const { src } = this.state;
        if (src <= 0) {
            return null;
        }

        const { pages } = this.props;
        const page_id = pages.filter((p) => p.page_contents_id === src)[0][
            "page_id"
        ];

        const values = [-1];
        const labels = [i18n.get("form.selectEmptyLabel")];

        let status;
        let changes = false;
        for (const p of pages) {
            if (p.page_id === page_id && p.page_contents_id !== src) {
                values.push(p.page_contents_id);

                status = i18n.get(`userDocForm.status${p.user_status}`);
                labels.push(`${status} / ${p.user_name} / ${p.page_name}`);
                changes = true;
            }
        }

        return (
            <FieldContainer>
                <FieldTitle>{i18n.get("merge.dst")}</FieldTitle>
                {!changes && (
                    <AlreadyUpToDate>
                        {i18n.get("merge.alreadyUpToDate")}
                    </AlreadyUpToDate>
                )}
                {changes && (
                    <Select
                        id="dst_page"
                        name="dst_page"
                        key="dst-page"
                        values={values.map((v) => v.toString())}
                        labels={labels}
                        fullWidth={true}
                        value={this.state.dst.toString()}
                        onChange={this.onChangeDst}
                    />
                )}
            </FieldContainer>
        );
    }

    toggleAll = (_name: string, enabled: boolean) =>
        this.setState({ srcAll: enabled });

    allSwitch = () => (
        <SwitchContainer>
            <Switch
                name="merge.all"
                messageId="merge.all"
                width={50}
                height={50}
                onChange={this.toggleAll}
                enabled={this.state.srcAll}
            />
        </SwitchContainer>
    );

    fields() {
        if (this.props.pages.length === 0) {
            return null;
        }

        return (
            <Fields>
                {this.allSwitch()}
                {this.src()}
                {this.dst()}
            </Fields>
        );
    }

    onConfirm = () => {
        const { src, dst } = this.state;

        let src_page_id = -1;
        let dst_page_id = -1;
        for (let p of this.props.pages) {
            if (src === p.page_contents_id) {
                src_page_id = p.page_id;
            }

            if (dst === p.page_contents_id) {
                dst_page_id = p.page_id;
            }
        }

        if (
            src_page_id === dst_page_id &&
            src_page_id > 0 &&
            src > 0 &&
            dst > 0
        ) {
            this.props.onConfirm(src_page_id, src, dst);
        }
    };

    onReturnClick = () => navigate.back();

    buttons() {
        const { src, dst } = this.state;
        const bothValid = src > 0 && dst > 0;

        return (
            <ButtonActionGroup>
                {bothValid && (
                    <Button
                        size={BTN_SMALL}
                        type={TYPE_SUCCESS}
                        messageId="merge.confirmRequest"
                        onClick={this.onConfirm}
                    />
                )}

                <Button
                    size={BTN_SMALL}
                    type={TYPE_SECONDARY}
                    messageId="form.return"
                    onClick={this.onReturnClick}
                />
            </ButtonActionGroup>
        );
    }

    noChanges() {
        if (this.props.pages.length === 0) {
            return <NoChanges>{i18n.get("merge.noChanges")}</NoChanges>;
        }

        return null;
    }

    render() {
        return (
            <Container>
                <Heading>{i18n.get("merge.title")}</Heading>
                <Description>{i18n.get("merge.description")}</Description>

                {this.buttons()}
                <Notification />

                {this.fields()}
                {this.noChanges()}
            </Container>
        );
    }
}

export default connect(mapState)(Header);
